import { useBreakpointStyles } from '@components/BreakpointStyle'
import { CookiebotConfig } from '@components/Cookiebot/Cookiebot'
import Layout from '@components/Layout'
import ArticleListWithShowMore from '@components/magazine/ArticleListWithShowMore'
import { Seo } from '@components/SEO'
import {
    Box,
    Button,
    FilterChip,
    Grid2,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Scroller,
    styled,
    Typography,
    Visible,
} from '@ds/components'
import { useCurrentUser } from '@hooks/useCurrentUser'
import { useTrackGtmPageView } from '@utils/useTrackGtmPageView'
import { graphql, useStaticQuery } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import { useState } from 'react'
import { Taxonomy } from '../../types/common'
import { MagazinePage } from '../../types/magazine'

const Header = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    height: 40,
    alignItems: 'center',
    justifyContent: 'space-between',
}))

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
}))

const PageTemplate = () => {
    const query = useStaticQuery(graphql`
        query {
            allWpMagazinePage {
                nodes {
                    title
                    date
                    excerpt
                    featuredImage {
                        node {
                            localFile {
                                childImageSharp {
                                    gatsbyImageData
                                }
                            }
                            altText
                            description
                        }
                    }
                    id
                    uri
                    topics {
                        nodes {
                            databaseId
                            name
                        }
                    }
                    sections {
                        nodes {
                            databaseId
                            name
                        }
                    }
                }
            }
            allWpSection {
                nodes {
                    name
                    id
                    databaseId
                }
            }
            allWpTopic {
                nodes {
                    name
                    databaseId
                }
            }
        }
    `)
    const { classes } = useBreakpointStyles()
    const { data: currentUser } = useCurrentUser()

    const articles: MagazinePage[] = query.allWpMagazinePage.nodes
    const topics: { name: string; id: string; databaseId: number }[] = query.allWpTopic.nodes
    const sections: { name: string; id: string; databaseId: number }[] = query.allWpSection.nodes

    let urlParams
    if (typeof window !== 'undefined') {
        try {
            urlParams = new URLSearchParams(window.location.search)
        } catch {
            //
        }
    }
    const [isSelected, setSelected] = useState(urlParams ? parseInt(urlParams.get('section') || '0', 10) || 0 : 0)

    const topicHeadline = topics.find((element) => element.databaseId === isSelected)
    const canResetAll = Boolean(isSelected)
    const resetAll = () => {
        setSelected(0)
    }

    function showArticlesPerSection(databaseId: number) {
        const filteredArticles = articles.filter((page: MagazinePage) =>
            page.sections.nodes.some((sectionNode: Taxonomy) => sectionNode.databaseId === databaseId),
        )

        filteredArticles.sort(
            (articleA, articleB) => new Date(articleB.date).getTime() - new Date(articleA.date).getTime(),
        )
        return filteredArticles
    }

    const showArticlesPerTopic = (databaseId: number) => {
        const filteredArticles = articles.filter((page: MagazinePage) =>
            page.topics.nodes.some((sectionNode: Taxonomy) => sectionNode.databaseId === databaseId),
        )

        filteredArticles.sort(
            (articleA, articleB) => new Date(articleB.date).getTime() - new Date(articleA.date).getTime(),
        )
        return filteredArticles
    }

    const articleList = (
        <>
            {canResetAll ? (
                <Box>
                    <Typography variant="h2" sx={{ mb: 1 }}>
                        {topicHeadline?.name}
                    </Typography>
                    {showArticlesPerTopic(isSelected).length > 0 ? (
                        <ArticleListWithShowMore
                            initialData={showArticlesPerTopic(isSelected)}
                            initialArticleDisplay={3}
                            showMoreAmount={3}
                        />
                    ) : (
                        <Paper sx={{ p: 1 }}>
                            <Typography variant="h4" sx={{ p: 2 }}>
                                No articles found
                            </Typography>
                        </Paper>
                    )}
                </Box>
            ) : null}
            {sections.map(({ name, id, databaseId }) => (
                <Box key={`section-${id}`} sx={canResetAll ? { display: 'none' } : undefined}>
                    <Typography variant="h2" sx={{ mb: 1 }}>
                        {name}
                    </Typography>
                    <ArticleListWithShowMore
                        initialData={showArticlesPerSection(databaseId)}
                        initialArticleDisplay={3}
                        showMoreAmount={5}
                    />
                </Box>
            ))}
        </>
    )

    const topicPills = topics.map(({ name, databaseId }) => (
        <FilterChip
            sx={{ m: 1 }}
            key={`topic-chip-${databaseId}`}
            label={name}
            onClick={() => (databaseId === isSelected ? setSelected(0) : setSelected(databaseId))}
            isSelected={databaseId === isSelected}
        />
    ))

    return (
        <Layout differentTopPadding={currentUser ? 9 : 0}>
            <Box sx={{ mb: 3 }}>
                <StaticImage
                    src="../../images/magazine/overview_hero_image_with_logo.png"
                    alt="InterNations Magazine - Perspectives on Expat Life"
                />
            </Box>

            <Box className={classes.showMobile}>
                <Typography variant="h2" sx={{ mb: 1 }}>
                    Magazine
                </Typography>
                <StyledPaper>
                    <Header>
                        <Typography variant="h4">Topics&nbsp;</Typography>
                        {/* {toggleSelectAllButton} */}
                    </Header>
                    <Visible desktop>{topicPills}</Visible>
                    <Scroller className="mobile-only">
                        <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>{topicPills}</Box>
                    </Scroller>
                </StyledPaper>
                {articleList}
            </Box>

            <Box className={classes.showDesktop}>
                <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 3, md: 3 }}>
                        <Box
                            display="flex"
                            flexDirection="row"
                            height={40}
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Typography variant="h2" sx={{ mb: 1 }}>
                                Topics
                            </Typography>
                            {canResetAll ? (
                                <Button variant="text" onClick={resetAll}>
                                    Reset all
                                </Button>
                            ) : null}
                        </Box>

                        <Paper>
                            <List disablePadding>
                                {topics.map(({ name, databaseId }) => {
                                    return (
                                        <li key={`topics-${databaseId}`}>
                                            <ListItemButton
                                                selected={databaseId === isSelected}
                                                divider
                                                onClick={() =>
                                                    databaseId === isSelected ? setSelected(0) : setSelected(databaseId)
                                                }
                                            >
                                                <ListItemText
                                                    primary={name}
                                                    sx={{
                                                        component: 'div',
                                                        variant: 'subtitle2',
                                                        color: databaseId === isSelected ? 'textPrimary' : 'primary',
                                                        paddingLeft: 1,
                                                    }}
                                                />
                                            </ListItemButton>
                                        </li>
                                    )
                                })}
                            </List>
                        </Paper>
                    </Grid2>

                    <Grid2 size={{ xs: 9, md: 9 }}>{articleList}</Grid2>
                </Grid2>
            </Box>
        </Layout>
    )
}

export default PageTemplate
export const Head = () => {
    useTrackGtmPageView()

    return (
        <>
            <html lang="en" />
            <Seo
                title="Magazine | InterNations"
                description="The leading network & guide for expats in 420 cities worldwide. Connect with fellow expatriates at top events and receive tips & advice on expat life."
            />
            <CookiebotConfig />
        </>
    )
}
